import React, { useState, useEffect } from 'react';
import {
  Grid,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  Box,
  Card,
  CardMedia,
  CardContent,
  Button,
  Chip,
  Autocomplete,
  Slider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { getFirestore, collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { isDressLinkedToAccount } from '../utils/dressOwnerUtils';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

const SearchScreen: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const accentSoft = alpha(theme.palette.primary.main, 0.22);
  const accentGlow = alpha(theme.palette.primary.main, 0.28);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [filters, setFilters] = useState({
    taille: '',
    categorie: '',
    marque: '',
    marqueCustom: '',
    prixMin: 0,
    prixMax: 500,
    distance: 10,
    couleur: '',
    style: ''
  });
  const [dateDebut, setDateDebut] = useState<Date | null>(null);
  const [dateFin, setDateFin] = useState<Date | null>(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectingPeriod, setSelectingPeriod] = useState(false);
  const [tempStartDate, setTempStartDate] = useState<Date | null>(null);

  // Suggestions de recherche populaires
  const searchSuggestions = [
    'Robe de bal',
    'Robe de cocktail',
    'Robe mini',
    'Robe de mariée',
    'Robe de soirée',
    'Robe longue',
    'Robe courte',
    'Robe rouge',
    'Robe noire',
    'Robe blanche',
    'Robe bleue',
    'Robe rose',
    'Robe verte',
    'Robe dorée',
    'Robe argentée',
    'Robe à paillettes',
    'Robe vintage',
    'Robe moderne',
    'Robe classique',
    'Robe bohème',
    'Robe romantique',
    'Robe sexy',
    'Robe décontractée',
    'Robe de travail',
    'Robe de plage',
    'Robe d\'été',
    'Robe d\'hiver',
    'Robe de printemps',
    'Robe de gala',
    'Robe de réception'
  ];

  // Suggestions personnalisées
  const personalizedSuggestions = [
    'Robes tendance pour un mariage',
    'Dernières ajoutées près de vous',
    'Robes de cocktail pour soirée',
    'Robes vintage à la mode',
    'Robes de luxe pour gala',
    'Robes bohème pour festival',
    'Robes de travail élégantes',
    'Robes de plage estivales',
    'Robes de bal princesse',
    'Robes romantiques pour rendez-vous'
  ];

  // Options de filtres
  const tailles = ['32', '34', '36', '38', '40', '42', '44', '46', '48'];
  const categories = [
    'Robe longue',
    'Robe courte',
    'Robe de cocktail',
    'Robe de bal',
    'Robe de mariée',
    'Robe de soirée',
    'Robe de travail',
    'Robe décontractée',
    'Robe de plage',
    'Robe vintage'
  ];
  const marques = [
    'Chanel',
    'Dior',
    'Versace',
    'Valentino',
    'Elie Saab',
    'Zimmermann',
    'Self-Portrait',
    'Réalisation Par',
    'Rent the Runway',
    'Autre'
  ];
  const couleurs = [
    'Noir',
    'Blanc',
    'Rouge',
    'Bleu',
    'Rose',
    'Vert',
    'Jaune',
    'Violet',
    'Doré',
    'Argenté',
    'Beige',
    'Marron'
  ];
  const styles = [
    'Classique',
    'Moderne',
    'Vintage',
    'Bohème',
    'Romantique',
    'Sexy',
    'Élégant',
    'Décontracté',
    'Chic',
    'Sophistiqué'
  ];

  // Fonction pour obtenir la valeur hexadécimale des couleurs
  const getColorValue = (colorName: string): string => {
    const colorMap: { [key: string]: string } = {
      'Noir': '#000000',
      'Blanc': '#FFFFFF',
      'Rouge': '#FF0000',
      'Bleu': '#0000FF',
      'Rose': '#FFC0CB',
      'Vert': '#008000',
      'Jaune': '#FFFF00',
      'Violet': '#800080',
      'Doré': '#FFD700',
      'Argenté': '#C0C0C0',
      'Beige': '#F5F5DC',
      'Marron': '#8B4513'
    };
    return colorMap[colorName] || '#CCCCCC';
  };

  // Charger les robes depuis Firestore
  const [dresses, setDresses] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [results, setResults] = useState<any[]>([]);

  // Charger les robes depuis Firestore
  useEffect(() => {
    const loadDresses = async () => {
      try {
        setLoading(true);
        const db = getFirestore();
        const dressesCollection = collection(db, 'dresses');
        const dressesSnapshot = await getDocs(dressesCollection);
        
        const dressesData: any[] = [];
        const candidates: { id: string; data: Record<string, unknown> }[] = [];
        dressesSnapshot.forEach((d) => {
          candidates.push({ id: d.id, data: d.data() as Record<string, unknown> });
        });

        const ownerChecks = await Promise.all(
          candidates.map(async ({ id, data }) => {
            const link = await isDressLinkedToAccount(db, data);
            if (!link.valid) return null;
            return {
              id,
              title: (data.title as string) || (data.name as string) || 'Robe sans titre',
              price: (data.price as number) || 0,
              image: (data.images as string[])?.[0] || (data.image as string) || '',
              location: (data.location as string) || 'Non spécifié',
            };
          })
        );

        ownerChecks.forEach((row) => {
          if (row) dressesData.push(row);
        });
        
        setDresses(dressesData);
        setResults(dressesData);
      } catch (error) {
        console.error('Erreur lors du chargement des robes:', error);
        setDresses([]);
        setResults([]);
      } finally {
        setLoading(false);
      }
    };

    loadDresses();
  }, []);

  const performSearch = () => {
    const q = (searchQuery || '').toLowerCase().trim();
    const filtered = dresses.filter((d) => {
      const matchesQuery = q
        ? d.title.toLowerCase().includes(q) || (d.location || '').toLowerCase().includes(q)
        : true;
      const brandQuery = (filters.marque === 'Autre' ? filters.marqueCustom : filters.marque) || '';
      const matchesBrand = brandQuery
        ? (d.title + ' ' + (d.location || '')).toLowerCase().includes(brandQuery.toLowerCase())
        : true;
      const matchesPrice = d.price >= (filters.prixMin || 0) && d.price <= (filters.prixMax || 9999);
      return matchesQuery && matchesBrand && matchesPrice;
    });
    setResults(filtered);
  };

  // Mettre à jour les résultats quand les robes ou les filtres changent
  useEffect(() => {
    performSearch();
  }, [dresses, searchQuery, filters]);

  return (
    <Box
      sx={{
        px: { xs: 2, sm: 3, md: 5 },
        py: { xs: 3, sm: 5, md: 6 },
        maxWidth: 1320,
        mx: 'auto',
        backgroundColor: 'background.default',
        minHeight: '60vh',
      }}
    >
      <Typography
        variant="overline"
        sx={{ letterSpacing: '0.35em', color: 'text.secondary', display: 'block', mb: 1 }}
      >
        Catalogue
      </Typography>
      <Typography variant="h4" component="h1" sx={{ fontFamily: '"Cormorant Garamond", serif', fontWeight: 500, mb: 1, color: 'text.primary' }}>
        Rechercher une robe
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4, maxWidth: 520 }}>
        Affinez par style, dates et budget, même expérience sur mobile et bureau.
      </Typography>

      {/* Barre de recherche principale */}
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
          Barre de recherche (par taille, couleur, style, marque, prix, localisation)
        </Typography>
        
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={7}>
            <Autocomplete
              freeSolo
              options={[...searchSuggestions, ...personalizedSuggestions]}
              value={searchQuery}
              onInputChange={(event, newValue) => {
                setSearchQuery(newValue || '');
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  variant="outlined"
                  placeholder="Rechercher une robe..."
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <Button
                          variant="contained"
                          size="medium"
                          onClick={performSearch}
                          sx={{
                            backgroundColor: 'primary.main',
                            '&:hover': { backgroundColor: 'primary.dark' },
                            textTransform: 'none',
                            borderRadius: 2
                          }}
                        >
                          Rechercher
                        </Button>
                      </InputAdornment>
                    )
                  }}
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <SearchIcon sx={{ mr: 1, fontSize: 20 }} />
                  {option}
                </Box>
              )}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<FilterListIcon />}
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              sx={{ 
                backgroundColor: 'primary.main', 
                '&:hover': { backgroundColor: 'primary.dark' },
                height: '56px',
                whiteSpace: 'nowrap'
              }}
            >
              Filtres avancés
            </Button>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={performSearch}
              sx={{
                height: '56px',
                backgroundColor: 'primary.main',
                color: 'white',
                boxShadow: 'none',
                '&:hover': { backgroundColor: 'primary.dark' }
              }}
            >
              Rechercher
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Suggestions personnalisées */}
      <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', display: 'flex', alignItems: 'center' }}>
          <TrendingUpIcon sx={{ mr: 1 }} />
          Suggestions personnalisées
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {personalizedSuggestions.map((suggestion, index) => (
            <Chip
              key={index}
              label={suggestion}
              onClick={() => setSearchQuery(suggestion)}
              sx={{
                backgroundColor: '#f5f5f5',
                '&:hover': { backgroundColor: 'primary.main', color: 'white' },
                cursor: 'pointer'
              }}
            />
          ))}
        </Box>
      </Paper>

      {/* Filtres avancés */}
      {showAdvancedFilters && (
        <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
            Filtres avancés
          </Typography>
          
          <Grid container spacing={3}>
            {/* Taille */}
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Taille</InputLabel>
                <Select
                  value={filters.taille}
                  onChange={(e) => setFilters({...filters, taille: e.target.value})}
                  label="Taille"
                >
                  <MenuItem value="">Toutes les tailles</MenuItem>
                  {tailles.map(taille => (
                    <MenuItem key={taille} value={taille}>{taille}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Catégorie */}
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Catégorie</InputLabel>
                <Select
                  value={filters.categorie}
                  onChange={(e) => setFilters({...filters, categorie: e.target.value})}
                  label="Catégorie"
                >
                  <MenuItem value="">Toutes les catégories</MenuItem>
                  {categories.map(categorie => (
                    <MenuItem key={categorie} value={categorie}>{categorie}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Marque */}
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Marque / Designer</InputLabel>
                <Select
                  value={filters.marque}
                  onChange={(e) => setFilters({...filters, marque: e.target.value})}
                  label="Marque / Designer"
                >
                  <MenuItem value="">Toutes les marques</MenuItem>
                  {marques.map(marque => (
                    <MenuItem key={marque} value={marque}>{marque}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              {filters.marque === 'Autre' && (
                <Box sx={{ mt: 1 }}>
                  <TextField
                    fullWidth
                    label="Saisir une marque"
                    placeholder="Ex: Jacquemus"
                    value={filters.marqueCustom}
                    onChange={(e) => setFilters({ ...filters, marqueCustom: e.target.value })}
                  />
                </Box>
              )}
            </Grid>

            {/* Distance */}
            <Grid item xs={12} md={3}>
              <Box>
                <Typography gutterBottom sx={{ color: 'primary.main' }}>
                  Distance: {filters.distance} km
                </Typography>
                <Slider
                  value={filters.distance}
                  onChange={(e, newValue) => setFilters({...filters, distance: newValue as number})}
                  min={1}
                  max={50}
                  step={1}
                  valueLabelDisplay="auto"
                  sx={{ color: 'primary.main' }}
                />
              </Box>
            </Grid>

            {/* Budget */}
            <Grid item xs={12} md={6}>
              <Box>
                <Typography gutterBottom sx={{ color: 'primary.main' }}>
                  Budget: {filters.prixMin}€ - {filters.prixMax}€
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <TextField
                    type="number"
                    label="Prix min"
                    value={filters.prixMin}
                    onChange={(e) => setFilters({...filters, prixMin: Number(e.target.value)})}
                    sx={{ width: 120 }}
                  />
                  <Typography>-</Typography>
                  <TextField
                    type="number"
                    label="Prix max"
                    value={filters.prixMax}
                    onChange={(e) => setFilters({...filters, prixMax: Number(e.target.value)})}
                    sx={{ width: 120 }}
                  />
                </Box>
              </Box>
            </Grid>

            {/* Couleur */}
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Couleur</InputLabel>
                <Select
                  value={filters.couleur}
                  onChange={(e) => setFilters({...filters, couleur: e.target.value})}
                  label="Couleur"
                >
                  <MenuItem value="">Toutes les couleurs</MenuItem>
                  {couleurs.map(couleur => (
                    <MenuItem key={couleur} value={couleur}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            borderRadius: '50%',
                            backgroundColor: getColorValue(couleur),
                            border: '1px solid #ccc'
                          }}
                        />
                        {couleur}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Style */}
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Style</InputLabel>
                <Select
                  value={filters.style}
                  onChange={(e) => setFilters({...filters, style: e.target.value})}
                  label="Style"
                >
                  <MenuItem value="">Tous les styles</MenuItem>
                  {styles.map(style => (
                    <MenuItem key={style} value={style}>{style}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              sx={{ backgroundColor: 'primary.main', '&:hover': { backgroundColor: 'primary.dark' } }}
            >
              Appliquer les filtres
            </Button>
            <Button
              variant="outlined"
              onClick={() => setFilters({
                taille: '',
                categorie: '',
                marque: '',
                marqueCustom: '',
                prixMin: 0,
                prixMax: 500,
                distance: 10,
                couleur: '',
                style: ''
              })}
            >
              Réinitialiser
            </Button>
          </Box>
        </Paper>
      )}

      {/* Dates de location */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', display: 'flex', alignItems: 'center' }}>
          Dates de location
        </Typography>
        
        {/* Sélection de dates avec calendrier */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Sélectionnez votre période de location
          </Typography>
          
          {/* Résumé de sélection */}
          {(dateDebut || dateFin) && (
            <Box sx={{ mb: 3, p: 2, backgroundColor: '#f8f9fa', borderRadius: 2, border: '1px solid', borderColor: 'primary.main' }}>
              <Typography variant="body1" sx={{ color: 'primary.main', fontWeight: 'bold', mb: 1 }}>
                {dateDebut && dateFin ? 
                  `${Math.ceil((dateFin.getTime() - dateDebut.getTime()) / (1000 * 60 * 60 * 24))} jour(s) sélectionné(s)` : 
                  'Sélectionnez vos dates'
                }
              </Typography>
              {dateDebut && dateFin && (
                <Typography variant="body2" color="text.secondary">
                  {dateDebut.toLocaleDateString('fr-FR', { 
                    day: 'numeric', 
                    month: 'short', 
                    year: 'numeric' 
                  })} - {dateFin.toLocaleDateString('fr-FR', { 
                    day: 'numeric', 
                    month: 'short', 
                    year: 'numeric' 
                  })}
                </Typography>
              )}
              {(dateDebut || dateFin) && (
                <Button
                  size="small"
                  onClick={() => {
                    setDateDebut(null);
                    setDateFin(null);
                    setTempStartDate(null);
                    setSelectingPeriod(false);
                  }}
                  sx={{ mt: 1, color: 'primary.main' }}
                >
                  Effacer les dates
                </Button>
              )}
            </Box>
          )}

          {/* Calendrier personnalisé, largeur limitée */}
          <Box sx={{ 
            mb: 2, 
            p: 1.5, 
            maxWidth: 320,
            mx: 'auto',
            backgroundColor: '#fafafa', 
            borderRadius: 2,
            border: '1px solid #e0e0e0',
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
          }}>
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold', color: 'primary.main', display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.95rem' }}>
              📅 Calendrier de sélection
            </Typography>
            
            {selectingPeriod && (
              <Alert 
                severity="info" 
                sx={{ 
                  mb: 1,
                  py: 0.25,
                  backgroundColor: '#e3f2fd',
                  border: '1px solid #90caf9',
                  borderRadius: 1,
                  '& .MuiAlert-message': { py: 0.5 }
                }}
              >
                <Typography variant="caption" sx={{ display: 'block' }}>
                  <strong>Sélection en cours...</strong> Cliquez sur la date de fin
                </Typography>
              </Alert>
            )}
            
            {/* Navigation du calendrier */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              mb: 1,
              py: 0.75,
              px: 1,
              gap: 0.5,
              backgroundColor: 'white',
              borderRadius: 1,
              boxShadow: '0 1px 2px rgba(0,0,0,0.06)'
            }}>
              <Button 
                onClick={() => {
                  setCurrentMonth(prev => {
                    const newMonth = new Date(prev);
                    newMonth.setMonth(prev.getMonth() - 1);
                    return newMonth;
                  });
                }}
                variant="outlined"
                size="small"
                sx={{ 
                  minWidth: 0,
                  px: 1,
                  py: 0.25,
                  fontSize: '0.7rem',
                  borderColor: 'primary.main', 
                  color: 'primary.main',
                  '&:hover': {
                    borderColor: 'primary.dark',
                    backgroundColor: '#f5f5f5'
                  }
                }}
              >
                ← Préc.
              </Button>
              <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 'bold', textTransform: 'capitalize', fontSize: '0.8rem', textAlign: 'center', flex: 1, lineHeight: 1.2 }}>
                {currentMonth.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
              </Typography>
              <Button 
                onClick={() => {
                  setCurrentMonth(prev => {
                    const newMonth = new Date(prev);
                    newMonth.setMonth(prev.getMonth() + 1);
                    return newMonth;
                  });
                }}
                variant="outlined"
                size="small"
                sx={{ 
                  minWidth: 0,
                  px: 1,
                  py: 0.25,
                  fontSize: '0.7rem',
                  borderColor: 'primary.main', 
                  color: 'primary.main',
                  '&:hover': {
                    borderColor: 'primary.dark',
                    backgroundColor: '#f5f5f5'
                  }
                }}
              >
                Suiv. →
              </Button>
            </Box>

            {/* Grille du calendrier */}
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', 
              gap: 0.5,
              mb: 1,
              p: 1,
              backgroundColor: 'white',
              borderRadius: 1,
              boxShadow: '0 1px 2px rgba(0,0,0,0.06)'
            }}>
              {/* En-têtes des jours */}
              {['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'].map(day => (
                <Typography 
                  key={day} 
                  variant="caption" 
                  sx={{ 
                    textAlign: 'center', 
                    fontWeight: 'bold', 
                    py: 0.35,
                    px: 0,
                    fontSize: '0.65rem',
                    color: 'primary.main',
                    backgroundColor: '#f8f9fa',
                    borderRadius: 0.5
                  }}
                >
                  {day}
                </Typography>
              ))}
              
              {/* Dates du calendrier */}
              {(() => {
                const year = currentMonth.getFullYear();
                const month = currentMonth.getMonth();
                const firstDay = new Date(year, month, 1);
                const startDate = new Date(firstDay);
                startDate.setDate(startDate.getDate() - firstDay.getDay());
                
                const dates = [];
                for (let i = 0; i < 42; i++) {
                  const date = new Date(startDate);
                  date.setDate(startDate.getDate() + i);
                  dates.push(date);
                }
                
                return dates.map((date, index) => {
                  const isCurrentMonth = date.getMonth() === month;
                  const isToday = date.toDateString() === new Date().toDateString();
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  const isPast = date < today;
                  
                  const isSelected = dateDebut && dateFin && date >= dateDebut && date <= dateFin;
                  const isStart = dateDebut && date.getTime() === dateDebut.getTime();
                  const isEnd = dateFin && date.getTime() === dateFin.getTime();
                  const isTempStart = tempStartDate && date.getTime() === tempStartDate.getTime();
                  
                  return (
                    <Button
                      key={index}
                      onClick={() => {
                        if (isPast) return;
                        
                        if (!selectingPeriod) {
                          setTempStartDate(date);
                          setSelectingPeriod(true);
                        } else {
                          let startDate: Date;
                          let endDate: Date;
                          
                          if (tempStartDate && date > tempStartDate) {
                            startDate = tempStartDate;
                            endDate = date;
                          } else if (tempStartDate && date < tempStartDate) {
                            startDate = date;
                            endDate = tempStartDate;
                          } else {
                            startDate = date;
                            endDate = date;
                          }
                          
                          setDateDebut(startDate);
                          setDateFin(endDate);
                          setSelectingPeriod(false);
                          setTempStartDate(null);
                        }
                      }}
                      disabled={isPast}
                      sx={{
                        minWidth: 0,
                        width: '100%',
                        maxWidth: '100%',
                        height: 34,
                        minHeight: 34,
                        p: 0,
                        borderRadius: 1,
                        backgroundColor: isSelected ? 'primary.main' : 
                                       isTempStart ? accentSoft :
                                       isCurrentMonth ? (isToday ? '#e3f2fd' : '#f8f9fa') : '#fafafa',
                        color: isSelected ? 'white' : 
                               isCurrentMonth ? '#333' : '#ccc',
                        border: isStart || isEnd ? `2px solid ${theme.palette.primary.main}` :
                                isToday ? '2px solid #1976d2' : '1px solid transparent',
                        fontWeight: isSelected || isStart || isEnd ? 'bold' : 'normal',
                        fontSize: '0.75rem',
                        lineHeight: 1,
                        transition: 'background-color 0.15s ease',
                        '&:hover': {
                          backgroundColor: isPast ? 'transparent' : (isSelected ? 'primary.dark' : accentSoft),
                          transform: 'none',
                          boxShadow: isPast ? 'none' : `0 1px 4px ${accentGlow}`,
                        },
                        '&:disabled': {
                          backgroundColor: '#f5f5f5',
                          color: '#ccc',
                          cursor: 'not-allowed'
                        }
                      }}
                    >
                      {date.getDate()}
                    </Button>
                  );
                });
              })()}
            </Box>

            {/* Légende */}
            <Box sx={{ 
              display: 'flex', 
              gap: 1, 
              rowGap: 0.5,
              flexWrap: 'wrap', 
              justifyContent: 'center', 
              mt: 1,
              pt: 1,
              borderTop: '1px solid #e0e0e0'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ 
                  width: 12, 
                  height: 12, 
                  flexShrink: 0,
                  backgroundColor: 'primary.main', 
                  borderRadius: 0.5,
                  boxShadow: '0 1px 2px rgba(0,0,0,0.15)'
                }} />
                <Typography variant="caption" sx={{ fontWeight: 'medium', fontSize: '0.65rem' }}>Sélectionné</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ 
                  width: 12, 
                  height: 12, 
                  flexShrink: 0,
                  backgroundColor: '#f8f9fa', 
                  borderRadius: 0.5,
                  border: '1px solid #e0e0e0'
                }} />
                <Typography variant="caption" sx={{ fontWeight: 'medium', fontSize: '0.65rem' }}>Disponible</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ 
                  width: 12, 
                  height: 12, 
                  flexShrink: 0,
                  backgroundColor: '#f5f5f5', 
                  borderRadius: 0.5,
                  border: '1px solid #e0e0e0'
                }} />
                <Typography variant="caption" sx={{ fontWeight: 'medium', fontSize: '0.65rem' }}>Passé</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ 
                  width: 12, 
                  height: 12, 
                  flexShrink: 0,
                  backgroundColor: '#e3f2fd', 
                  borderRadius: 0.5,
                  border: '2px solid #1976d2'
                }} />
                <Typography variant="caption" sx={{ fontWeight: 'medium', fontSize: '0.65rem' }}>Aujourd'hui</Typography>
              </Box>
            </Box>
          </Box>
          
          
          {/* Affichage de la durée - Déjà dans le résumé ci-dessus */}
          {dateDebut && dateFin && (
            <Box sx={{ mt: 2, p: 2, backgroundColor: '#e8f5e8', borderRadius: 2, border: '1px solid #4caf50' }}>
              <Typography variant="body2" sx={{ color: '#2e7d32', fontWeight: 'bold' }}>
                📊 Durée de location: {Math.ceil((dateFin.getTime() - dateDebut.getTime()) / (1000 * 60 * 60 * 24))} jour(s)
              </Typography>
            </Box>
          )}
        </Box>


        {/* Bouton de recherche avec les dates */}
        {(dateDebut || dateFin) && (
          <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              sx={{ 
                backgroundColor: 'primary.main', 
                '&:hover': { backgroundColor: 'primary.dark' },
                flex: 1
              }}
            >
              🔍 Rechercher les robes disponibles
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                setDateDebut(null);
                setDateFin(null);
              }}
            >
              Effacer les dates
            </Button>
          </Box>
        )}
      </Paper>

      <Box sx={{ mt: 6, mb: 3 }}>
        <Typography variant="h6" sx={{ fontFamily: '"Cormorant Garamond", serif', fontWeight: 500 }}>
          Pièces disponibles
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: '0.12em' }}>
          {results.length} résultat{results.length > 1 ? 's' : ''}
        </Typography>
      </Box>
      
      <Grid container spacing={{ xs: 2.5, sm: 3, md: 4 }}>
        {loading ? (
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          </Grid>
        ) : results.length === 0 ? (
          <Grid item xs={12}>
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h6" color="text.secondary">
                Aucune robe disponible pour le moment
              </Typography>
            </Box>
          </Grid>
        ) : (
          results.map((dress) => (
          <Grid item xs={12} sm={6} md={4} key={dress.id}>
            <Card
              sx={{
                cursor: 'pointer',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                border: `1px solid ${alpha(theme.palette.divider, 1)}`,
                boxShadow: 'none',
                borderRadius: 2,
                bgcolor: '#fff',
                overflow: 'hidden',
                transition: 'transform 0.45s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.45s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 24px 56px ${alpha(theme.palette.common.black, 0.08)}`,
                },
                '&:hover .MuiCardMedia-root': {
                  transform: 'scale(1.03)',
                },
              }}
              onClick={() => {
                navigate(`/dress/${dress.id}`);
              }}
            >
              <Box sx={{ position: 'relative', bgcolor: alpha(theme.palette.secondary.light, 0.25) }}>
                <CardMedia
                  component="img"
                  image={dress.image}
                  alt={dress.title}
                  className="MuiCardMedia-root"
                  sx={{
                    width: '100%',
                    aspectRatio: '3 / 4',
                    height: 'auto',
                    objectFit: 'cover',
                    display: 'block',
                    transition: 'transform 0.65s ease',
                    transformOrigin: 'center center',
                  }}
                />
              </Box>
              <CardContent sx={{ pt: 3, pb: 3, px: 2.5, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                <Typography
                  variant="overline"
                  sx={{ letterSpacing: '0.2em', color: 'text.secondary', fontSize: '0.65rem', mb: 0.75 }}
                >
                  Location
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: '"Cormorant Garamond", serif',
                    fontWeight: 600,
                    fontSize: '1.15rem',
                    lineHeight: 1.25,
                    mb: 1.5,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {dress.title}
                </Typography>
                <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 600, letterSpacing: '0.06em', mb: 1 }}>
                  {dress.price} € <Box component="span" sx={{ fontWeight: 400, color: 'text.secondary' }}>/ jour</Box>
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: '0.04em', mt: 'auto' }}>
                  {dress.location}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          ))
        )}
      </Grid>
    </Box>
  );
};

export default SearchScreen; 